import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import AppHeader from '../../components/AppHeader';
import Icon from '@mdi/react';
import { mdiChartBar } from '@mdi/js';
import { ticketApi } from '../../api/ticket';
import { formatCurrencyFromMinor } from '../../utils/currency';
import '../Common.css';
import '../Dashboard.css';
import '../header.css';
import './TicketingStats.css';

/**
 * TicketingStats Page — display-only ticket sales metrics
 *
 * Shows each published event as a card with per-type sold/remaining counts,
 * total revenue, and a capacity progress bar.
 * Sub-portal feature page — navigates back to /ticketing.
 */
const TicketingStats = () => {
    const { user } = useAuth();
    const navigate = useNavigate();

    const [eventStats, setEventStats] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const loadStats = useCallback(async () => {
        if (!user?.organisationId) return;
        try {
            setIsLoading(true);
            const stats = await ticketApi.getStats();
            const now = new Date();
            stats.sort((a, b) => {
                const aDate = new Date(a.eventStartDateTime || 0);
                const bDate = new Date(b.eventStartDateTime || 0);
                const aIsPast = aDate < now;
                const bIsPast = bDate < now;
                if (aIsPast !== bIsPast) return aIsPast ? 1 : -1;
                return aIsPast ? bDate - aDate : aDate - bDate;
            });
            setEventStats(stats);
        } catch (error) {
            console.error('Error loading ticket stats:', error);
            toast.error('Failed to load ticket stats.');
        } finally {
            setIsLoading(false);
        }
    }, [user?.organisationId]);

    useEffect(() => {
        loadStats();
    }, [loadStats]);

    const formatEventDate = (isoString) => {
        if (!isoString) return 'Date TBA';
        return new Date(isoString).toLocaleDateString('en-AU', {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const getCapacityClass = (percent) => {
        if (percent > 80) return 'high';
        if (percent >= 50) return 'medium';
        return '';
    };

    return (
        <div className="dashboard-container org-console ticketing-stats-page">
            <AppHeader />

            <div className="dashboard-main">
                <div className="dashboard-content-wrapper">

                    <div className="sub-portal-header-card">
                        <div className="sub-portal-header-title-row">
                            <button
                                onClick={() => navigate('/ticketing')}
                                className="back-nav-btn"
                            >
                                ← Ticketing
                            </button>
                            <h1 className="sub-portal-header-title">
                                <Icon path={mdiChartBar} size={0.9} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                                Ticket Stats
                            </h1>
                        </div>
                        <p className="sub-portal-header-subtitle">
                            Sales metrics for your published events.
                        </p>
                    </div>

                    {isLoading ? (
                        <div className="loading-state">Loading ticket stats...</div>
                    ) : eventStats.length > 0 ? (
                        <div className="stats-events-list">
                            {eventStats.map((event) => {
                                const capacityPercent = event.totalCapacity > 0
                                    ? Math.round((event.totalSold / event.totalCapacity) * 100)
                                    : 0;

                                return (
                                    <div key={event.id} className="content-card stats-event-card">
                                        <div className="stats-card-header">
                                            <h5 className="stats-card-title">{event.eventTitle}</h5>
                                            <span className={`stats-card-badge ${event.eventStartDateTime && new Date(event.eventStartDateTime) < new Date() ? 'ended' : ''}`}>
                                                {event.eventStartDateTime && new Date(event.eventStartDateTime) < new Date() ? 'Ended' : 'Live'}
                                            </span>
                                        </div>

                                        <p className="stats-event-date">
                                            {formatEventDate(event.eventStartDateTime)}
                                        </p>

                                        <div className="stats-ticket-types">
                                            {event.ticketTypes.map((type) => (
                                                <div key={type.name} className="stats-ticket-type-row">
                                                    <span className="stats-type-name">{type.name}</span>
                                                    <span className="stats-type-counts">
                                                        {type.sold} sold / {type.remaining} remaining
                                                    </span>
                                                    <span className="stats-type-checkin">
                                                        {type.checkedIn ?? 0} checked in
                                                    </span>
                                                    <span className="stats-type-price">
                                                        {formatCurrencyFromMinor(type.price, type.currency)}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>

                                        <p className="stats-revenue">
                                            Total revenue: {formatCurrencyFromMinor(event.totalRevenue, event.currency)}
                                        </p>

                                        <div className="stats-capacity-track">
                                            <div
                                                className={`stats-capacity-fill ${getCapacityClass(capacityPercent)}`}
                                                style={{ width: `${capacityPercent}%` }}
                                            />
                                        </div>
                                        <span className="stats-capacity-label">
                                            {capacityPercent}% capacity sold ({event.totalSold} / {event.totalCapacity})
                                        </span>

                                        {event.totalSold > 0 && (
                                            <>
                                                <div className="stats-checkin-track">
                                                    <div
                                                        className="stats-checkin-fill"
                                                        style={{ width: `${Math.round(((event.totalCheckedIn ?? 0) / event.totalSold) * 100)}%` }}
                                                    />
                                                </div>
                                                <span className="stats-capacity-label">
                                                    {Math.round(((event.totalCheckedIn ?? 0) / event.totalSold) * 100)}% checked in ({event.totalCheckedIn ?? 0} / {event.totalSold})
                                                </span>
                                            </>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="empty-state content-card">
                            <p>No published events yet.</p>
                            <p>Publish an event from Manage Tickets to see stats here.</p>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
};

export default TicketingStats;
